import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SelectButtonCombo } from "components/formFields/SelectButtonCombo";

import { i18n } from "services/i18nService";
import { getItemRecord } from "services/sosInventoryService/domainLogic";
import { afterTouchLine } from "services/sosInventoryService/fulfillmentTransaction/afterTouchLine";
import { updateLineWithItem as updateFulfillmentLineWithItem } from "services/sosInventoryService/fulfillmentTransaction/domainLogic";
import {
  ITEM_CALCULATED_FIELDS as PICK_TICKET_ITEM_CALCULATED_FIELDS,
  ITEM_CALCULATED_FIELDS_NO_ON_HAND as PICK_TICKET_ITEM_CALCULATED_FIELDS_NO_ON_HAND,
} from "services/sosInventoryService/pickTicket/schema";
import { updateWorkOrderLineWithItem } from "services/sosInventoryService/productionTransaction/domainLogic";
import {
  ITEM_CALCULATED_FIELDS as SHIPMENT_ITEM_CALCULATED_FIELDS,
  ITEM_CALCULATED_FIELDS_NO_ON_HAND as SHIPMENT_ITEM_CALCULATED_FIELDS_NO_ON_HAND,
} from "services/sosInventoryService/shipment/schema";
import { getRecord } from "services/sosInventoryService/sosApi";
import { ITEM_CALCULATED_FIELDS as WORK_ORDER_ITEM_CALCULATED_FIELDS } from "services/sosInventoryService/workOrder/schema";
import { setPageDirty } from "services/utility/edit";
import { getAddOrderPlaceholder } from "services/utility/misc";
import {
  getBaseUom,
  getUomConversionFromUomReference,
} from "services/utility/uoms";

import { useCustomerData } from "hooks/useCustomerData";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { getObjectFromFullString, OBJECT_TYPES } from "appConstants";

const OBJECT_TYPE_CONFIG = {
  [OBJECT_TYPES.PICK_TICKET.fullString]: {
    updateLineWithItemFn: updateFulfillmentLineWithItem,
    itemCalculatedFields: PICK_TICKET_ITEM_CALCULATED_FIELDS,
    itemCalculatedFieldsNoOnHand: PICK_TICKET_ITEM_CALCULATED_FIELDS_NO_ON_HAND,
  },
  [OBJECT_TYPES.SHIPMENT.fullString]: {
    updateLineWithItemFn: updateFulfillmentLineWithItem,
    itemCalculatedFields: SHIPMENT_ITEM_CALCULATED_FIELDS,
    itemCalculatedFieldsNoOnHand: SHIPMENT_ITEM_CALCULATED_FIELDS_NO_ON_HAND,
  },
  [OBJECT_TYPES.WORK_ORDER.fullString]: {
    updateLineWithItemFn: updateWorkOrderLineWithItem,
    itemCalculatedFields: WORK_ORDER_ITEM_CALCULATED_FIELDS,
  },
};

export function AddCustomerOrder(props) {
  const { customer, lineHandler, objectType, label, record } = props;

  const isPickTicket = objectType === OBJECT_TYPES.PICK_TICKET.fullString;
  const isFulfillmentType =
    objectType === OBJECT_TYPES.PICK_TICKET.fullString ||
    objectType === OBJECT_TYPES.SHIPMENT.fullString;

  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );

  const dispatch = useDispatch();

  const [value, setValue] = useState(null);
  const options = useCustomerData(customer, objectType);

  // reset the selection whenever the customer changes
  useEffect(() => setValue(null), [customer]);

  async function addOrder() {
    if (!value) {
      return;
    }
    const { objectType: srcObjectType, txnId } = value;
    dispatch(editModalLoadingIndicatorOn());
    const sourceOrder = await getRecord(srcObjectType, txnId);
    const nonZeroLines = sourceOrder.lines.filter(
      ({ shipped, picked, quantity }) =>
        isPickTicket ? picked.lt(quantity) : shipped.lt(quantity)
    );
    const transactionType = getObjectFromFullString(srcObjectType).shortString;

    const newLines = await Promise.all(
      nonZeroLines.map(async (line) => {
        const {
          updateLineWithItemFn,
          itemCalculatedFields,
          itemCalculatedFieldsNoOnHand,
        } = OBJECT_TYPE_CONFIG[objectType];
        const itemFields =
          isFulfillmentType && showQuantityOnBuildsAndShipments
            ? itemCalculatedFields
            : itemCalculatedFieldsNoOnHand;
        const item = await getItemRecord(
          line.item.id,
          record.location?.id,
          record.date,
          itemFields
        );
        const lineWithItem = await updateLineWithItemFn(item, line, record);
        const baseLine = {
          ...lineWithItem,
          linkedTransaction: {
            id: sourceOrder.id,
            refNumber: sourceOrder.number,
            lineNumber: line.lineNumber,
            transactionType,
          },
          uom: line.uom || lineWithItem.uom,
          quantity: line.quantity.minus(line.shipped),
          ...(isFulfillmentType &&
            showQuantityOnBuildsAndShipments && {
              onhand: item.onhand.div(
                getUomConversionFromUomReference(
                  line.uom || getBaseUom(item.uoms),
                  item.uoms
                )
              ),
            }),
        };
        delete baseLine.id;
        return afterTouchLine(baseLine, "quantity");
      })
    );
    if (newLines.length) {
      setPageDirty();
      lineHandler({ type: "append", newLines });
    }

    dispatch(editModalLoadingIndicatorOff());
  }

  return (
    <SelectButtonCombo
      key={options}
      disableClearable
      maxWidth="12rem"
      minWidth="12rem"
      selectLabel={label}
      buttonLabel={i18n("global.Add")}
      value={value}
      onValueChange={(_, { id }) => setValue(options.find((o) => o.id === id))}
      onClick={addOrder}
      options={options}
      placeholder={getAddOrderPlaceholder(
        options,
        i18n("addOrder.SelectOrder")
      )}
      disabled={!Boolean(customer)}
    />
  );
}
